import { useTalkJSData } from "@platform/hooks/useChat";
import { useVideoMeetingDetails } from "@platform/store";

/**
 * The single TalkJS `conversation_id` an in-meeting chat surface should
 * bind to — the conversation matching the meeting's circle.
 *
 * Returns null when:
 * - `meetingDetails` hasn't loaded yet (no `circle` to match against)
 * - the session-init payload hasn't loaded (no `conversations` to look in)
 * - the user isn't a member of the meeting's circle (chat gated, or
 *   upstream provisioning hasn't caught up — same fall-through case
 *   `VideoMeetingChatContainer` already handles)
 *
 * `talkjsData.conversations` is program-scoped (one entry per accepted
 * circle the user belongs to in the meeting's program), but the in-
 * meeting Chatbox renders only one of those — the one for the meeting's
 * circle. Every in-meeting chat surface (Chatbox itself, sidebar tab
 * badge, controls-bar chat button badge) must agree on which single
 * conversation that is, otherwise the badge counts unread messages from
 * conversations the user can't open from inside the meeting. Centralized
 * here so all three call sites resolve identically.
 */
export function useMeetingChatConversationId(): string | null {
  const talkjsData = useTalkJSData();
  const { meetingDetails } = useVideoMeetingDetails();

  const meetingCircleId = meetingDetails?.circle ?? null;
  if (meetingCircleId == null) {
    return null;
  }

  return (
    talkjsData?.conversations.find((c) => c.circle_id === meetingCircleId)
      ?.talkjs_conversation_id ?? null
  );
}

export default useMeetingChatConversationId;
